/**
 * DashboardPage — D01 (PR-KPI-SNAPSHOT Fase 5).
 *
 * KPI dinamici: Appuntamenti oggi, Prossimi oggi, Promemoria.
 * KPI statici da snapshot: Pazienti attivi, Da ricontattare, Compleanni.
 * Il pulsante «Aggiorna» ricarica solo i dinamici.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAuth } from '../../hooks/useAuth';
import { firstName } from '../../lib/role-display';
import { PageHeader } from '../../components/ui/PageHeader';
import { KpiCard } from '../../components/ui/KpiCard';
import { Card } from '@shared/components/ui/Card';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { Badge } from '@shared/components/ui/Badge';
import { PROMEMORIA_ESITO_LABELS } from '@shared/promemoria/labels';
import { useDashboardData } from './hooks/useDashboardData';
import { useDashboardPromemoriaLog } from './hooks/useDashboardPromemoriaLog';
import {
  formatKpiSnapshotOrario,
  useKpiSnapshot,
  type KpiSnapshotState,
} from './hooks/useKpiSnapshot';
import { usePazienteDisplayNames } from '../../hooks/usePazienteDisplayNames';
import { parseAppuntamentoDate } from '../../services/agenda';

const PROSSIMI_OGGI_MAX = 14;

/**
 * Saluto contestuale alla parte di giornata.
 * 5:00-12:59  → "Buongiorno"
 * 13:00-17:59 → "Buon pomeriggio"
 * 18:00-4:59  → "Buonasera"
 */
function greeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 13) return 'Buongiorno';
  if (h < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

function formatOraAppuntamento(data: Date | string): string {
  try {
    return format(parseAppuntamentoDate(data as string), 'HH:mm');
  } catch {
    return '';
  }
}

function formatDataRome(yyyyMmDd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd);
  if (!m) return yyyyMmDd;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function pazienteLabel(
  resolve: ReturnType<typeof usePazienteDisplayNames>['resolve'],
  pazienteId: string
): string {
  const d = resolve(pazienteId);
  if (d.kind === 'name') return `${d.cognome} ${d.nome}`.trim();
  if (d.kind === 'adminId') return pazienteId;
  return 'Paziente non disponibile';
}

function snapshotListEmpty(snapshot: KpiSnapshotState): {
  title: string;
  description: string;
} | null {
  if (snapshot.kind === 'absent') {
    return {
      title: 'Elenco non ancora disponibile',
      description: 'I dati notturni non sono ancora stati calcolati.',
    };
  }
  if (snapshot.kind === 'unavailable') {
    return {
      title: 'Non disponibile',
      description: 'Questi elenchi non sono visibili per il tuo ruolo.',
    };
  }
  return null;
}

export function DashboardPage() {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const { data, loading, refresh } = useDashboardData();
  const snapshot = useKpiSnapshot();
  const promemoria = useDashboardPromemoriaLog();

  const snapshotIds =
    snapshot.kind === 'ready'
      ? [...snapshot.doc.daRicontattareIds, ...snapshot.doc.compleanniIds]
      : [];
  const prossimiIds = (data?.prossimiOggi ?? []).map((a) => a.pazienteId);
  const promemoriaIds = promemoria.items.map((r) => r.pazienteId);
  const names = usePazienteDisplayNames([
    ...snapshotIds,
    ...prossimiIds,
    ...promemoriaIds,
  ]);

  const today = useMemo(() => new Date(), []);
  const dateLabel = useMemo(
    () =>
      format(today, 'EEEE d MMMM yyyy', { locale: it }).replace(
        /^./,
        (c) => c.toUpperCase(),
      ),
    [today],
  );

  const name = firstName(user?.displayName, user?.email);

  const prossimiAll = data?.prossimiOggi ?? [];
  const prossimiShown = prossimiAll.slice(0, PROSSIMI_OGGI_MAX);
  const showVediAgenda = prossimiAll.length > PROSSIMI_OGGI_MAX;

  const snapshotLoading = snapshot.kind === 'loading';
  const snapshotReady = snapshot.kind === 'ready';
  const snapshotStale = snapshotReady && snapshot.stale;
  const listEmpty = snapshotListEmpty(snapshot);

  const orario =
    snapshotReady ? formatKpiSnapshotOrario(snapshot.doc.generatedAtIso) : null;

  return (
    <div>
      <PageHeader
        eyebrow={dateLabel}
        title={`${greeting(today)}, ${name}`}
        subtitle="Ecco il riepilogo di oggi."
        actions={
          <button
            type="button"
            onClick={() => {
              refresh();
              promemoria.refresh();
            }}
            className="text-sm text-text-secondary hover:text-text-main transition-colors"
            aria-label="Aggiorna dati dashboard"
          >
            Aggiorna
          </button>
        }
      />

      {orario && (
        <p
          className="font-body-sm text-body-sm text-text-secondary -mt-4 md:-mt-6 mb-6 flex flex-wrap items-center gap-2"
          data-testid="kpi-snapshot-freschezza"
        >
          <span>
            Pazienti attivi, da ricontattare e compleanni: aggiornati alle{' '}
            {orario.time} del {orario.date}.
          </span>
          {snapshotStale && <Badge tone="warning">Non aggiornato</Badge>}
        </p>
      )}

      {/* KPI row — 2 card */}
      <section
        aria-label="Indicatori chiave"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <KpiCard
          label="Appuntamenti oggi"
          value={loading || data === null ? '—' : String(data.appuntamentiOggiCount)}
          icon="calendar_today"
          loading={loading}
        />
        <KpiCard
          label="Pazienti attivi"
          hint="Non archiviati, con almeno un appuntamento passato negli ultimi 12 mesi."
          value={
            snapshotReady ? String(snapshot.doc.pazientiAttiviCount) : '—'
          }
          icon="group"
          loading={snapshotLoading}
          sub={snapshotStale ? 'Non aggiornato' : undefined}
          subTone="warning"
        />
      </section>

      {/* Prossimi appuntamenti oggi — full width */}
      <section
        aria-label="Prossimi appuntamenti oggi"
        className="mt-6"
      >
        <Card>
          <div className="flex items-start justify-between gap-3 mb-3">
            <h2 className="font-headline-md text-headline-md text-text-main">
              Prossimi appuntamenti oggi
            </h2>
            {showVediAgenda && (
              <Link
                to="/app/agenda"
                className="text-sm text-primary shrink-0 hover:underline"
              >
                Vedi in Agenda
              </Link>
            )}
          </div>
          {loading ? (
            <SkeletonList rows={4} />
          ) : prossimiShown.length ? (
            <ul
              className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0"
              aria-label="Appuntamenti di oggi"
            >
              {prossimiShown.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between py-1.5 border-b border-border-subtle"
                >
                  <span className="text-sm font-medium text-text-main truncate pr-2">
                    {pazienteLabel(names.resolve, a.pazienteId)}
                  </span>
                  <span className="text-sm text-text-secondary shrink-0 tabular-nums">
                    {formatOraAppuntamento(a.data)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon="event_busy"
              title="Nessun appuntamento"
              description="Non ci sono appuntamenti programmati per oggi."
              size="sm"
            />
          )}
        </Card>
      </section>

      {/* Da ricontattare | Promemoria — 50/50 */}
      <section
        aria-label="Sezioni di dettaglio"
        className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4"
      >
        <Card>
          <h2 className="font-headline-md text-headline-md text-text-main">
            Pazienti da ricontattare
          </h2>
          <p className="mt-1 mb-3 font-body-sm text-body-sm text-text-secondary">
            Ultima visita completata e nessun appuntamento futuro. Prime 20 in
            ordine alfabetico.
          </p>
          {snapshotLoading ? (
            <SkeletonList rows={3} />
          ) : snapshotReady && snapshot.doc.daRicontattareIds.length ? (
            <ul className="space-y-2" aria-label="Pazienti da ricontattare">
              {snapshot.doc.daRicontattareIds.map((id) => (
                <li
                  key={id}
                  className="flex items-center py-1.5 border-b border-border-subtle last:border-0"
                >
                  <span className="text-sm font-medium text-text-main truncate">
                    {pazienteLabel(names.resolve, id)}
                  </span>
                </li>
              ))}
            </ul>
          ) : listEmpty ? (
            <EmptyState
              icon="contact_phone"
              title={listEmpty.title}
              description={listEmpty.description}
              size="sm"
            />
          ) : (
            <EmptyState
              icon="contact_phone"
              title="Nessun follow-up"
              description="Nessun paziente da ricontattare al momento."
              size="sm"
            />
          )}
        </Card>

        <Card>
          <div className="flex items-start justify-between gap-3 mb-3">
            <h2 className="font-headline-md text-headline-md text-text-main">
              Promemoria inviati automatici
            </h2>
            <Link
              to="/app/impostazioni/promemoria/log"
              className="text-sm text-primary shrink-0 hover:underline"
            >
              Log promemoria
            </Link>
          </div>
          {promemoria.adminRequiresTier2 ||
          (promemoria.error && !promemoria.loading) ? (
            <EmptyState
              icon="mark_email_unread"
              title="Log non disponibile"
              description="Consulta il log completo dalle Impostazioni."
              size="sm"
              action={
                <Link
                  to="/app/impostazioni/promemoria/log"
                  className="text-sm text-primary hover:underline"
                >
                  Apri Log promemoria
                </Link>
              }
            />
          ) : promemoria.loading ? (
            <SkeletonList rows={3} />
          ) : promemoria.items.length ? (
            <ul
              className="space-y-2"
              aria-label="Promemoria inviati automatici"
              data-testid="home-promemoria-list"
            >
              {promemoria.items.map((row) => (
                <li
                  key={row.id}
                  className="grid grid-cols-[auto_1fr_auto] gap-2 items-center py-1.5 border-b border-border-subtle last:border-0 text-sm"
                  data-paziente-id={row.pazienteId}
                  data-esito={row.esito}
                  data-modo={row.modo}
                >
                  <span className="text-text-secondary tabular-nums shrink-0">
                    {formatDataRome(row.dataAppuntamentoRome)}
                  </span>
                  <span className="font-medium text-text-main truncate">
                    {pazienteLabel(names.resolve, row.pazienteId)}
                  </span>
                  <span className="text-text-secondary shrink-0 text-right">
                    {PROMEMORIA_ESITO_LABELS[row.esito]}
                    {row.esito === 'errore_api' && row.errorCode
                      ? ` (${row.errorCode})`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon="mark_email_unread"
              title="Nessun promemoria recente"
              description="Gli invii automatici in modalità live compariranno qui."
              size="sm"
            />
          )}
        </Card>
      </section>

      {/* Compleanni — full width */}
      <section aria-label="Compleanni della settimana" className="mt-6">
        <Card>
          <h2 className="font-headline-md text-headline-md text-text-main mb-3">
            Compleanni della settimana
          </h2>
          {snapshotLoading ? (
            <SkeletonList rows={2} />
          ) : snapshotReady && snapshot.doc.compleanniIds.length ? (
            <ul className="space-y-2" aria-label="Compleanni della settimana">
              {snapshot.doc.compleanniIds.map((id) => (
                <li
                  key={id}
                  className="flex items-center py-1.5 border-b border-border-subtle last:border-0"
                >
                  <span className="text-sm font-medium text-text-main truncate">
                    {pazienteLabel(names.resolve, id)}
                  </span>
                </li>
              ))}
            </ul>
          ) : listEmpty ? (
            <EmptyState
              icon="cake"
              title={listEmpty.title}
              description={listEmpty.description}
              size="sm"
            />
          ) : (
            <EmptyState
              icon="cake"
              title="Nessun compleanno"
              description="Nessun compleanno questa settimana."
              size="sm"
            />
          )}
        </Card>
      </section>
    </div>
  );
}

/** Skeleton per le liste nelle card di dettaglio durante il caricamento. */
function SkeletonList({ rows }: { rows: number }) {
  return (
    <div className="space-y-2" aria-label="Caricamento" role="status">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-8 rounded bg-bg-card animate-pulse"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export default DashboardPage;
